import React, { useState, useEffect } from 'react'
import { injectIntl, WrappedComponentProps, FormattedMessage } from 'react-intl'
import history from 'browserHistory'
import getConstants from '../../constants'

import { BreadcrumbGroup } from 'shared-global-ec-uikit/components/dist/react/esm/components/breadcrumb-group'
import { Breadcrumb } from 'shared-global-ec-uikit/components/dist/react/esm/components/breadcrumb'
import { Button } from 'shared-global-ec-uikit/components/dist/react/esm/components/button'
import StoresMap, {
  Props as StoresMapProps,
  OnSearchThisAreaType,
} from 'components/StoresMap'

import StoreDetailsPreview from './components/StoreDetailsPreview'
import Filters from './components/Filters'
import ActiveFilters, {
  countActiveFilters,
  Props as ActiveFiltersProps,
} from './components/ActiveFilters'
import Results from './components/Results'
import SeeAllStores from './components/SeeAllStores'
import Search, { Props as SearchProps } from './components/Search'
import { selector as mapStateToProps } from './selector'

import {
  ButtonLayout,
  ButtonToggle,
  HeaderPage,
  Modal,
  TemplateStoreLocator,
} from 'shared-global-ec-uikit/components/dist/react/esm'
import { parseComponent } from 'helpers/cms'
import { connect } from 'react-redux'
import {
  MapFilterToQueryParamKey,
  UrlQueryParamValue,
} from 'services/Stores/typings'
import {
  Accessibility,
  ProductType,
  STORE_SIZE,
  StoreService,
} from 'services/Stores/constants'
import { toCamelCaseFromSnakeCase } from 'helpers/url'
import TagManager from 'react-gtm-module'
import { BRAND_LIST } from 'typings'
import { isNativeAppByUserAgent } from 'helpers/nativeApp'
import { isTabletSize } from 'helpers/responsive'

export type Props = ReturnType<typeof mapStateToProps> & {
  storeList: Array<Components.Schemas.StoreList>
  totalCount?: number
  selectedStore: Components.Schemas.StoreList
  selectedMarker: number | null
  isSearching: boolean
  onLoadMore: () => void
  onClearSearchTerm: () => void
  onStoreSelect: (storeId?: number) => void
  onMarkerSelect: (storeId?: number) => void
  onClearMarker: () => void
  onSearchThisArea: OnSearchThisAreaType
  onSeeAllStores: () => void
} & StoresMapProps &
  SearchProps &
  ActiveFiltersProps

type FilterProps = { [key: string]: boolean }

const brand = getConstants().BRAND

const triggerGA = (categories: string) => ({
  dataLayer: {
    event: 'trackEvent',
    brand: brand === BRAND_LIST.PL ? brand.toUpperCase() : brand,
    pageType: 'Storelocator',
    eventCategory: 'store_search',
    eventAction: 'filter_stores',
    eventLabel: categories,
  },
})

const StoreSearch = ({
  intl,
  intlMessages,
  apiKey,
  storeList,
  totalCount,
  initialCenter,
  initialZoom,
  filters,
  areaLabel,
  allAreasLabel,
  selectedStore,
  selectedMarker,
  isSearchingByMapArea,
  onSearchByLocation,
  onKeywordChange,
  onAreaChange,
  isSearching,
  onSearch,
  onSearchThisArea,
  onClearLocation,
  onFilter,
  onLoadMore,
  areas,
  onClearFilters,
  onStoreSelect,
  onMarkerSelect,
  onClearMarker,
  searchTerm,
  onClearSearchTerm,
  onSeeAllStores,
  cms,
  isFetchingCMS,
  countryListEU,
}: Props & WrappedComponentProps) => {
  const [filterModalIsOpen, toggleFilterModal] = useState(false)
  const [storeDetailsIsOpen, toggleStoreDetails] = useState(false)
  const departmentFilterKeys = Object.keys(filters.departmentFilters)
  const storeServiceFilterKeys = Object.keys(filters.storeServiceFilters)
  const accessibilityFilterKeys = Object.keys(filters.accessFilters)
  const queryParams = new URLSearchParams(window.location.search)
  // combined all query params in one object
  const [queryParamsValues, setQueryParamsValues] = useState({
    product: queryParams.get('product_type') || '',
    services: queryParams.get('services_type') || '',
    accessibility: queryParams.get('accessibility') || '',
  })
  const CONSTANTS = getConstants()
  // function to update search params in url
  const updateParamsInUrl = (
    queryParams: string,
    key: MapFilterToQueryParamKey | '',
    setAll: boolean = false,
  ): void => {
    const updatedParams = new URLSearchParams(window.location.search)
    if (queryParams) {
      switch (key) {
        case MapFilterToQueryParamKey.product:
          updatedParams.set(UrlQueryParamValue.product, queryParams)
          break
        case MapFilterToQueryParamKey.services:
          updatedParams.set(UrlQueryParamValue.services, queryParams)
          break
        case MapFilterToQueryParamKey.accessibility:
          updatedParams.set(UrlQueryParamValue.accessibility, queryParams)
          break
        default:
          break
      }
    } else if (key) {
      updatedParams.delete(
        UrlQueryParamValue[key as keyof typeof UrlQueryParamValue],
      )
    }
    if (setAll) {
      queryParamsValues.product &&
        updatedParams.set(UrlQueryParamValue.product, queryParamsValues.product)
      queryParamsValues.services &&
        updatedParams.set(
          UrlQueryParamValue.services,
          queryParamsValues.services,
        )
      queryParamsValues.accessibility &&
        updatedParams.set(
          UrlQueryParamValue.accessibility,
          queryParamsValues.accessibility,
        )
    }
    if ((key || setAll) && updatedParams.keys()) {
      history.push({ search: updatedParams.toString() })
    } else {
      window.history.replaceState(
        window.history.state,
        '',
        `${getConstants().BASE_NAME}/`,
      )
    }
  }

  // function to get filter type
  const getFilterParamType = (filterKey: string) => {
    if (departmentFilterKeys.includes(filterKey))
      return MapFilterToQueryParamKey.product
    else if (storeServiceFilterKeys.includes(filterKey))
      return MapFilterToQueryParamKey.services
    else if (accessibilityFilterKeys.includes(filterKey))
      return MapFilterToQueryParamKey.accessibility
    return ''
  }

  // function to update state value for search param
  const updateQueryParams = (
    key: MapFilterToQueryParamKey,
    value: string,
    checked: boolean,
  ) => {
    setQueryParamsValues(prevParams => {
      const updatedParams = { ...prevParams }
      if (checked) {
        updatedParams[key] = prevParams[key]
          ? `${prevParams[key]},${value}`
          : value
      } else {
        updatedParams[key] = prevParams[key]
          .split(',')
          .filter(option => option !== value)
          .join(',')
      }
      // trigger GA event for all categories except store type
      let gaValue = updatedParams[key]
      for (const [item, value] of Object.entries(updatedParams)) {
        if (item !== key && value) {
          gaValue = gaValue ? `${gaValue}, ${value}` : value
        }
      }
      if (filters.selectedStoreSizeFilter)
        gaValue = gaValue
          ? `${filters.selectedStoreSizeFilter}, ${gaValue}`
          : filters.selectedStoreSizeFilter
      if (gaValue)
        TagManager.dataLayer(triggerGA(gaValue.toLowerCase() as string))
      updateParamsInUrl(updatedParams[key], key)
      return updatedParams
    })
  }

  // set initial filter values from url
  useEffect(() => {
    const getFilteredValues = (query: string, filterKeys: Array<string>) =>
      query
        .split(',')
        .filter(value => filterKeys.includes(toCamelCaseFromSnakeCase(value)))
    const genders = getFilteredValues(
      queryParamsValues.product,
      departmentFilterKeys,
    )
    const storeServices = getFilteredValues(
      queryParamsValues.services,
      storeServiceFilterKeys,
    )
    const accessibility = getFilteredValues(
      queryParamsValues.accessibility,
      accessibilityFilterKeys,
    )
    const hasFilters =
      Boolean(genders.length) ||
      Boolean(storeServices.length) ||
      Boolean(accessibility.length)
    if (hasFilters) {
      let filtersObj: FilterProps = {}
      const addFiltersToObj = (filteredValues: Array<string>) => {
        filteredValues.forEach(
          filterKey => (filtersObj[toCamelCaseFromSnakeCase(filterKey)] = true),
        )
      }
      genders.length && addFiltersToObj(genders)
      storeServices.length &&
        getConstants().SHOW_STORE_SERVICE_FILTER[brand] &&
        addFiltersToObj(storeServices)
      accessibility.length && addFiltersToObj(accessibility)
      if (Object.keys(filtersObj).length) {
        onFilter(filtersObj)
        // trigger GA event on refresh for all categories
        TagManager.dataLayer(
          triggerGA(
            Object.keys(filtersObj)
              .join(',')
              .toLowerCase(),
          ),
        )
        setQueryParamsValues({
          ...queryParamsValues,
          product: genders.join(','),
          services: storeServices.join(','),
          accessibility: accessibility.join(','),
        })
        updateParamsInUrl('', '', true)
      } else {
        setQueryParamsValues({
          ...queryParamsValues,
          product: '',
          services: '',
          accessibility: '',
        })
        history.push({ search: '' })
      }
    }
  }, [])

  const handleOptionChange = (
    value: string,
    checked: boolean,
    filterParams: MapFilterToQueryParamKey,
  ) => {
    switch (filterParams) {
      case MapFilterToQueryParamKey.product:
        updateQueryParams(
          MapFilterToQueryParamKey.product,
          ProductType[value as keyof typeof ProductType],
          checked,
        )
        break
      case MapFilterToQueryParamKey.services:
        updateQueryParams(
          MapFilterToQueryParamKey.services,
          StoreService[value as keyof typeof StoreService],
          checked,
        )
        break
      case MapFilterToQueryParamKey.accessibility:
        updateQueryParams(
          MapFilterToQueryParamKey.accessibility,
          Accessibility[value as keyof typeof Accessibility],
          checked,
        )
        break
      default:
        break
    }
  }

  const onClearFilterHandler = () => {
    updateParamsInUrl('', '')
    setQueryParamsValues({
      ...queryParamsValues,
      product: '',
      services: '',
      accessibility: '',
    })
    onClearFilters()
  }

  const onFilterHandler = (filters: { [key: string]: boolean | string }) => {
    const filterKeys = Object.keys(filters)
    const filterParamType = getFilterParamType(filterKeys[0])

    if (filterKeys.length && filterParamType) {
      handleOptionChange(
        filterKeys[0],
        Boolean(filters[filterKeys[0]]),
        filterParamType,
      )
    } else {
      //trigger GA event for store type
      const storeSize = filters[STORE_SIZE] || ''
      const categories = Object.values(queryParamsValues)
        .filter(Boolean)
        .join(', ')
      const gaValue = categories
        ? storeSize
          ? `${storeSize}, ${categories}`
          : categories
        : storeSize
      if (gaValue)
        TagManager.dataLayer(triggerGA(gaValue.toString().toLowerCase()))
    }
    onFilter(filters)
  }

  const onSeeAllStoresHandler = () => {
    updateParamsInUrl('', '')
    setQueryParamsValues({
      ...queryParamsValues,
      product: '',
      services: '',
      accessibility: '',
    })
    onSeeAllStores()
  }

  const onFilterButtonClick = () => {
    toggleFilterModal(!filterModalIsOpen)
  }

  const onStoreSelection = (store: Components.Schemas.StoreList) => {
    onStoreSelect(store.id)
    toggleStoreDetails(true)
  }

  const onClearStore = () => {
    onStoreSelect()
    toggleStoreDetails(false)
    updateParamsInUrl('', '', true)
  }

  const getTotalCountString = (totalCount: string) => {
    return `${totalCount} ${intl.formatMessage({ id: 'text_stores' })}`
  }

  const getFilterModalTitle = (totalCount: number) => {
    const localizedString: string = intl.formatMessage({ id: 'text_filter' })

    if (!totalCount) {
      return localizedString
    }
    return localizedString + ' ' + getTotalCountString(totalCount.toString())
  }
  return (
    <>
      <Modal
        variant="large"
        // TODO: Disabling trapFocus is either a DS bug, or an implementation quirk - requires investigation
        trapFocus={false}
        title={getFilterModalTitle(totalCount!)}
        fixedButtonLayout={!(isNativeAppByUserAgent() && isTabletSize())}
        footer={
          <ButtonLayout variant="horizontal">
            <Button
              element="button"
              variant="primary"
              onClick={onFilterButtonClick}
            >
              <FormattedMessage id="text_apply" />
            </Button>
          </ButtonLayout>
        }
        fullWidthContentPosition="top"
        isOpen={filterModalIsOpen}
        closeLabel={intl.formatMessage({ id: 'text_close' })}
        onRequestClose={onFilterButtonClick}
      >
        <>
          <ActiveFilters
            areas={areas}
            filters={filters}
            onFilter={onFilterHandler}
            onClearFilters={onClearFilterHandler}
            onAreaChange={onAreaChange}
          />
          <Filters
            id="modalFilters"
            filters={filters}
            onFilter={onFilterHandler}
          />
        </>
      </Modal>
      <StoreDetailsPreview
        closeLabel={intl.formatMessage({ id: 'text_close' })}
        closeTitle={intl.formatMessage({ id: 'text_close' })}
        isOpen={storeDetailsIsOpen}
        onClose={onClearStore}
        store={selectedStore}
      />
      <>
        <TemplateStoreLocator
          className="shared-global-ec-uikit-page-wrapper__first-child"
          filterBadgeCount={
            countActiveFilters(filters)
              ? countActiveFilters(filters)
              : undefined
          }
          filterLabel={intl.formatMessage({ id: 'text_filter' })}
          map={
            <StoresMap
              className="map-container fr-ec-mb-spacing-05"
              apiKey={apiKey}
              dimensions={{ width: '100%', height: 326 }}
              initialCenter={initialCenter}
              initialZoom={initialZoom}
              storeList={storeList}
              intlMessages={intlMessages}
              isSearchThisAreaEnabled
              isSearchingByMapArea={isSearchingByMapArea}
              onSearchThisArea={onSearchThisArea}
              onStoreSelect={onStoreSelection}
              onMarkerSelect={onMarkerSelect}
              onClearMarker={onClearMarker}
              selectedMarker={selectedMarker}
            />
          }
          navigationPath={
            !isNativeAppByUserAgent() && (
              <BreadcrumbGroup>
                <Breadcrumb element="a" href={CONSTANTS.HOME_PAGE_URL}>
                  <FormattedMessage id="text_breadcrumbs_top" />
                </Breadcrumb>
                <Breadcrumb element="a" href={undefined}>
                  <FormattedMessage id="text_storesearch" />
                </Breadcrumb>
              </BreadcrumbGroup>
            )
          }
          pageHeading={
            <HeaderPage
              className="fr-ec-mb-spacing-05"
              title={intl.formatMessage({ id: 'text_storesearch' })}
              labelElement="h1"
            />
          }
          promotionalSection={
            <>
              {!isFetchingCMS && cms.cms.length > 0 && (
                <div className="fr-ec-mb-spacing-06">
                  {parseComponent({ list: cms.cms })}
                </div>
              )}
            </>
          }
          resultDetails={
            totalCount !== undefined ? (
              <ButtonToggle
                alignment="center"
                icon="filter"
                onClick={onFilterButtonClick}
                text="Filter"
                variant="iconWithLabel"
                isToggled
              />
            ) : null
          }
          resultDetailsCount={
            totalCount !== undefined
              ? getTotalCountString(totalCount.toString())
              : ''
          }
          searchFilter={
            totalCount !== undefined ? (
              <>
                <div className="fr-ec-mb-spacing-05">
                  <ActiveFilters
                    areas={areas}
                    filters={filters}
                    onFilter={onFilterHandler}
                    onClearFilters={onClearFilterHandler}
                    onAreaChange={onAreaChange}
                  />
                  <Filters
                    id="sideFilters"
                    filters={filters}
                    onFilter={onFilterHandler}
                  />
                </div>
              </>
            ) : (
              <></>
            )
          }
          searchOption={<SeeAllStores onSeeAllStores={onSeeAllStoresHandler} />}
          searchResult={
            <Results
              totalCount={totalCount}
              isSearching={isSearching}
              searchTerm={searchTerm}
              storeList={storeList}
              onLoadMore={onLoadMore}
              onFilterButtonClick={onFilterButtonClick}
              onStoreSelect={onStoreSelection}
              isSearchingByMapArea={isSearchingByMapArea}
              filterCount={countActiveFilters(filters)}
            />
          }
          searchSection={
            <Search
              countryListEU={countryListEU}
              filters={filters}
              areas={areas}
              areaLabel={areaLabel}
              allAreasLabel={allAreasLabel}
              onKeywordChange={onKeywordChange}
              onSearchByLocation={onSearchByLocation}
              onAreaChange={onAreaChange}
              onSearch={onSearch}
              onClearLocation={onClearLocation}
              onClearSearchTerm={onClearSearchTerm}
              searchTerm={searchTerm}
            />
          }
          toggleMinHeight
        />
      </>
    </>
  )
}

export default injectIntl(connect(mapStateToProps)(StoreSearch))
